import { createAsyncThunk } from '@reduxjs/toolkit';

import type { LoginType } from '../../auth/models/auth.model';
import type { Page } from '../../common/page/page.model';
import type { Pageable } from '../../common/page/pageable.model';
import { ApiError, parseApiError } from '../../models/api-error.enum';
import type { Comment } from '../model/comment.interface';
import type { PerksResponse } from '../model/perks-response.interface';
import type { Author, Post, PostCount, Tag } from '../model/post.interface';
import type { PostActionSearchRequest } from '../model/post-action-search-request';
import type { PostFilter } from '../model/post-filter.model';
import type { ContentTab, ContentType } from '../model/tab.interface';
import type { User, UserUpdateRequest } from '../model/user.interface';
import type { UserActionRequest } from '../model/user-action.inteface';
import { PerksService } from '../perks.services';

const perksService = new PerksService();

export const loadContentTabs = createAsyncThunk<ContentTab[], void, { rejectValue: ApiError }>(
  'perks/loadContentTabs',
  async (_, { rejectWithValue }) => {
    try {
      return await perksService.loadTabs();
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadTabContent = createAsyncThunk<
  Page<any>,
  { contentType: ContentType; cursor?: string | null; loadMore?: boolean },
  { rejectValue: ApiError }
>(
  'perks/loadTabContent',
  async ({ contentType, cursor }, { rejectWithValue }) => {
    try {
      return await perksService.loadTabContent(contentType, cursor);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadPerks = createAsyncThunk<
  PerksResponse,
  { query?: string },
  { rejectValue: { error: ApiError; loginTypes: LoginType[] } }
>('perks/loadPerks', async ({ query }, { rejectWithValue }) => {
  try {
    return await perksService.loadPerks(query);
  } catch (error: any) {
    return rejectWithValue({
      error: error.status === 401 ? ApiError.Unauthorized : ApiError.Other,
      loginTypes: error.status === 401 ? JSON.parse(error.error).loginTypes : null,
    });
  }
});

export const loadPosts = createAsyncThunk<
  Page<Post>,
  {
    pageable: Pageable;
    filter?: PostFilter;
    loadMore?: boolean;
  },
  { rejectValue: ApiError }
>('perks/loadPosts', async ({ pageable, filter, loadMore = false }, { rejectWithValue }) => {
  try {
    return await perksService.loadPosts(pageable, filter);
  } catch (error) {
    console.error('loadPosts thunk - error:', error);
    return rejectWithValue(parseApiError(error));
  }
});

export const loadPost = createAsyncThunk<Post, string, { rejectValue: ApiError }>(
  'perks/loadPost',
  async (id, { rejectWithValue }) => {
    try {
      return await perksService.loadPostById(id);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadPopularPosts = createAsyncThunk<
  Page<Post>,
  { request: PostActionSearchRequest; pageable: Pageable },
  { rejectValue: ApiError }
>('perks/loadPopularPosts', async ({ request, pageable }, { rejectWithValue }) => {
  try {
    return await perksService.loadPopularPosts(request, pageable);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadUser = createAsyncThunk<User, void, { rejectValue: ApiError }>(
  'perks/loadUser',
  async (_, { rejectWithValue }) => {
    try {
      return await perksService.getUser();
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const updateUser = createAsyncThunk<User, UserUpdateRequest, { rejectValue: ApiError }>(
  'perks/updateUser',
  async (request, { rejectWithValue }) => {
    try {
      return await perksService.updateUser(request);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const uploadAvatar = createAsyncThunk<
  User,
  { file: File; filename: string },
  { rejectValue: ApiError }
>('perks/uploadAvatar', async ({ file, filename }, { rejectWithValue }) => {
  try {
    return await perksService.uploadAvatar(file, filename).then(async (imageUploadResponse) => {
      // the image alone: the backend leaves every blank field as it is. Echoing the rest of the
      // profile sent the values from when the upload started, so a username saved while the upload
      // was in flight was overwritten with the old one when it landed.
      const updatedUser = await perksService.updateUser({
        imageFileSetId: imageUploadResponse.imageFileSetId,
      });
      // Server may not echo imageFileSetId back in the response, so preserve it
      // so that subsequent handleSave calls don't send null and clear the avatar.
      return { ...updatedUser, imageFileSetId: imageUploadResponse.imageFileSetId };
    });
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadPostCounts = createAsyncThunk<
  PostCount[],
  string | undefined,
  { rejectValue: ApiError }
>('perks/loadPostCounts', async (id, { rejectWithValue }) => {
  try {
    return await perksService.loadPostCounts(id);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadCustomContent = createAsyncThunk<
  { id: string; content: string },
  string,
  { rejectValue: ApiError }
>('perks/loadCustomContent', async (id, { rejectWithValue }) => {
  try {
    const content = await perksService.getCustomContent(id);
    return { id, content };
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const loadTags = createAsyncThunk<Page<Tag>, Pageable, { rejectValue: ApiError }>(
  'perks/loadTags',
  async (pageable, { rejectWithValue }) => {
    try {
      return await perksService.getTags(pageable);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadAuthors = createAsyncThunk<Page<Author>, Pageable, { rejectValue: ApiError }>(
  'perks/loadAuthors',
  async (id, { rejectWithValue }) => {
    try {
      return await perksService.getAuthors(id);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const loadPostComments = createAsyncThunk<
  { postId: string; parentCommentId: string | null; comments: Page<Comment> },
  {
    postId: string;
    parentCommentId: string | null;
    commentDepth: number;
    childPageSize: number;
    pageable: Pageable;
  },
  { rejectValue: ApiError }
>(
  'perks/loadPostComments',
  async (
    { postId, parentCommentId, commentDepth, childPageSize, pageable },
    { rejectWithValue }
  ) => {
    try {
      const comments = await perksService.getPostComments(
        postId,
        parentCommentId,
        commentDepth,
        childPageSize,
        pageable
      );
      return { postId, parentCommentId, comments };
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const postUserAction = createAsyncThunk<any, UserActionRequest, { rejectValue: ApiError }>(
  'perks/postUserAction',
  async (request, { rejectWithValue }) => {
    try {
      return await perksService.postUserAction(request);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  }
);

export const postComment = createAsyncThunk<
  Comment,
  { postId: string; parentCommentId: string | null; comment: Comment },
  { rejectValue: ApiError }
>('perks/postCommentResult', async ({ postId, parentCommentId, comment }, { rejectWithValue }) => {
  try {
    return await perksService.postComment(postId, parentCommentId, comment);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});

export const archiveComment = createAsyncThunk<
  Comment,
  { postId: string; commentId: string },
  { rejectValue: ApiError }
>('perks/archiveCommentResult', async ({ postId, commentId }, { rejectWithValue }) => {
  try {
    return await perksService.archiveComment(postId, commentId);
  } catch (error) {
    return rejectWithValue(parseApiError(error));
  }
});
